import React, { FC, useContext, useEffect, useMemo } from 'react';
import DataConsumptionContext from '../../../../../contexts/Billing/DataConsumptionContext';
import DataConsumptionMetricContext from '../../../../../contexts/Billing/DataConsumptionMetricContext';
import { InfoPaper } from '../../../components/ConsumptionData/InfoPaper';
import { localization } from './i18n/localization';
import { useContentLocalizer } from '../../../../../hooks/useContentLocalizer';
import { formatDayMonthLong } from '../../../../../utils/date';
import { BillingMetrics } from '../../../../../shared/BillingMetrics';
import { trackEvent } from '../../../../../api/Billing/Tracking';
import { REPORT_ADVANCED_DEU_CONSUMPTION_OPEN } from '../../../../../shared/SegmentEvents';
import colors from '../../../../../_charts.module.scss';
import { roundNextHundredth } from '../../../../../utils/numbers';
import { RightInfoPaperArea } from '../../styles';
import { ConsolidatedCenterPaper } from '../../../components/ConsumptionData/ConsolidatedPaperCenter';

const AdvancedDailyEngagedUserConsumptionComponent: FC = () => {
  const content = useContentLocalizer(localization) || localization.pt;
  const { dateToSearch, isMetricAllowed, tenant, loggedUser, trackingProps, periodInfo } =
    useContext(DataConsumptionContext);
  const {
    compiledAdvancedDailyEngagedUserDataState,
    isAdvancedDeuLoading,
    consolidatedAdvancedDeuApiRequisition,
    isDeuAiEnabled,
  } = useContext(DataConsumptionMetricContext);

  useEffect(() => {
    consolidatedAdvancedDeuApiRequisition(dateToSearch, (metrics: any) =>
      metrics.some((metric: any) => isMetricAllowed(metric)),
    );
  }, [dateToSearch]);

  const advancedDailyEngagedUserConsumption =
    compiledAdvancedDailyEngagedUserDataState.compiledAdvancedDailyEngagedUserConsumption;
  const advancedTotalDailyEngagedUserConsumption =
    compiledAdvancedDailyEngagedUserDataState.compiledTotalAdvancedDailyEngagedUserConsumption;
  const consolidatedContainsDataOnSelectedMonth =
    compiledAdvancedDailyEngagedUserDataState.compiledContainsDataOnSelectedMonth;

  const accordionData = [
    {
      type: BillingMetrics.dailyEngagedUser,
      title: content.header.modalContent.dailyEngagedUser.title,
      description: content.header.modalContent.dailyEngagedUser.description,
    },
    {
      type: BillingMetrics.prospectConversation,
      title: content.header.modalContent.prospectConversation.title,
      description: content.header.modalContent.prospectConversation.description,
    },
    {
      type: isDeuAiEnabled ? BillingMetrics.dailyEngagedUserAI : BillingMetrics.userConversation,
      title: isDeuAiEnabled ? content.header.modalContent.aiToken.title : content.header.modalContent.userConversation.title,
      description: isDeuAiEnabled
        ? content.header.modalContent.aiToken.description
        : content.header.modalContent.userConversation.description,
    },
    {
      type: BillingMetrics.activeMessage,
      title: content.header.modalContent.activeMessage.title,
      description: content.header.modalContent.activeMessage.description,
    },
  ];

  const header = {
    title: content.header.title,
    period: periodInfo,
    subtitle: content.header.description,
    learnMore: content.header.learnMore,
    modalContent: content.header.modalContent,
    buttonTitle: content.header.showMoreButton,
    pathname: '/advanced-deu-consumption',
    hasModalWithAccordion: true,
    accordionData: accordionData,
    buttonTrackEvent: () => trackEvent(REPORT_ADVANCED_DEU_CONSUMPTION_OPEN, tenant, loggedUser, trackingProps),
  };

  const InfoPaperData = [
    {
      type: BillingMetrics.dailyEngagedUser,
      value: consolidatedContainsDataOnSelectedMonth
        ? advancedTotalDailyEngagedUserConsumption.dailyEngagedUsersTotal.toLocaleString(content.locale)
        : '0',
      text: content.infoPaper.dailyEngagedUser,
    },
    {
      type: BillingMetrics.prospectConversation,
      value: consolidatedContainsDataOnSelectedMonth
        ? advancedTotalDailyEngagedUserConsumption.prospectConversationTotal.toLocaleString(content.locale)
        : '0',
      text: content.infoPaper.prospectConversation,
    },
    {
      type: isDeuAiEnabled ? BillingMetrics.dailyEngagedUserAI : BillingMetrics.userConversation,
      value: consolidatedContainsDataOnSelectedMonth
        ? advancedTotalDailyEngagedUserConsumption.userConversationTotal.toLocaleString(content.locale)
        : '0',
      text: isDeuAiEnabled ? content.infoPaper.aiToken : content.infoPaper.userConversation,
    },
    {
      type: BillingMetrics.activeMessage,
      value: consolidatedContainsDataOnSelectedMonth
        ? advancedTotalDailyEngagedUserConsumption.activeMessageTotal.toLocaleString(content.locale)
        : '0',
      text: content.infoPaper.activeMessage,
    },
  ];

  const infoPaperOptions = {
    headerTitle: content.infoPaper.headerTitle,
  };

  const infoPaperArea = (
    <RightInfoPaperArea flexDirection="row">
      {InfoPaperData.map((item) => (
        <InfoPaper
          key={item.type}
          justifyContent={'flex-start'}
          classNameTitle={'flex-start-margin'}
          title={item.value}
          text={item.text}
          hasCentralized={true}
        />
      ))}
    </RightInfoPaperArea>
  );

  const dailyItems = (advancedDailyEngagedUserConsumption || []).slice().reverse();

  const dailyItemsLabel = useMemo(() => {
    return dailyItems.map((item: any) => formatDayMonthLong(item.dateReference, content.locale));
  }, [dailyItems, content.locale]);

  const anchoredDayIndexes = useMemo(() => {
    const anchorDays = new Set(['01', '08', '15', '22']);

    return new Set(
      dailyItemsLabel
        .map((label: string, index: number) => {
          const day = label.slice(0, 2);
          return anchorDays.has(day) ? index : -1;
        })
        .filter((index: number) => index >= 0),
    );
  }, [dailyItemsLabel]);

  const dailyEngagedUserData = dailyItems.map((item: any) => item.dailyEngagedUser ?? 0);
  const prospectConversationData = dailyItems.map((item: any) => item.prospectConversation ?? 0);
  const userConversationData = dailyItems.map((item: any) => item.userConversation ?? 0);

  const thirdChartMetric = isDeuAiEnabled ? BillingMetrics.dailyEngagedUserAI : BillingMetrics.userConversation;

  const getCustomColor = (metricId: BillingMetrics) => {
    if (metricId === BillingMetrics.dailyEngagedUser) {
      return colors.ExtendedBlueLight;
    }
    if (metricId === BillingMetrics.prospectConversation) {
      return colors.Primary;
    }
    return colors.ExtendedCianDark;
  };

  const getCustomLabel = (metricId: BillingMetrics) => {
    if (metricId === BillingMetrics.dailyEngagedUser) {
      return content.chart.dailyEngagedUser;
    }
    if (metricId === BillingMetrics.prospectConversation) {
      return content.chart.prospectConversation;
    }
    return isDeuAiEnabled ? content.chart.aiToken : content.chart.userConversation;
  };

  const arrChartCustomLabel = [
    {
      backgroundColor: getCustomColor(BillingMetrics.dailyEngagedUser),
      text: getCustomLabel(BillingMetrics.dailyEngagedUser),
    },
    {
      backgroundColor: getCustomColor(BillingMetrics.prospectConversation),
      text: getCustomLabel(BillingMetrics.prospectConversation),
    },
    {
      backgroundColor: getCustomColor(thirdChartMetric),
      text: getCustomLabel(thirdChartMetric),
    },
  ];

  const arrChartDataset = [
    {
      label: getCustomLabel(BillingMetrics.dailyEngagedUser),
      data: dailyEngagedUserData,
      backgroundColor: getCustomColor(BillingMetrics.dailyEngagedUser),
    },
    {
      label: getCustomLabel(BillingMetrics.prospectConversation),
      data: prospectConversationData,
      backgroundColor: getCustomColor(BillingMetrics.prospectConversation),
    },
    {
      label: getCustomLabel(thirdChartMetric),
      data: userConversationData,
      backgroundColor: getCustomColor(thirdChartMetric),
    },
  ];

  const chartData = {
    chartType: 'BarChart',
    title: content.chart.title,
    labels: dailyItemsLabel,
    customLabels: arrChartCustomLabel,
    datasets: arrChartDataset,
  };

  const maxDailyTotal =
    Math.max(...dailyEngagedUserData, ...userConversationData, ...prospectConversationData, 0) || 1;

  const chartOptions = {
    maintainAspectRatio: false,
    barThickness: 8,
    locale: content.locale,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          align: 'start' as const,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value: any, index: number) => {
            return anchoredDayIndexes.has(index) ? dailyItemsLabel[index] : '';
          },
        },
      },
      y: consolidatedContainsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxDailyTotal * 1.1) }
        : { min: 0 },
    },
  };

  return (
    <ConsolidatedCenterPaper
      header={header}
      chart={{ chartData, chartOptions, redraw: false }}
      isLoading={isAdvancedDeuLoading}
      infoPapers={{ ...infoPaperOptions, content: infoPaperArea }}
    />
  );
};

export default AdvancedDailyEngagedUserConsumptionComponent;
